import { Briefcase, Copy, Link2, Pencil, Trash2, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import {
  GESTOR_TABLE_TH_CLASS,
  GESTOR_TABLE_TR_CLASS,
  GestorListTableShell,
  GestorTableAvatar,
} from '@/components/Gestores/GestorListTableShell'
import { cn } from '@/utils/cn'
import type { PjAgrada, PjAgradaCadastroInvite } from '@/components/PjAgrada/pjAgradaTypes'
import {
  PJ_AGRADA_STATUS_CLASS,
  PJ_AGRADA_STATUS_LABELS,
  isPlaceholderPhone,
} from '@/components/PjAgrada/pjAgradaStatus'
import {
  formatCnpj,
  formatContaBanco,
  formatCpf,
  formatEndereco,
  formatInviteTimeRemaining,
  formatPhone,
} from '@/components/PjAgrada/pjAgradaFormat'

type Props = {
  prestadores: PjAgrada[]
  loading: boolean
  search: string
  onSelect: (prestador: PjAgrada) => void
  onEdit?: (prestador: PjAgrada) => void
  onDelete?: (id: string) => void
  onInvite?: () => void
}

function documentLine(pj: PjAgrada): string | null {
  const parts: string[] = []
  if (pj.cpf) parts.push(formatCpf(pj.cpf))
  if (pj.cnpj) parts.push(formatCnpj(pj.cnpj))
  return parts.length > 0 ? parts.join(' · ') : null
}

async function copyCadastroInviteLink(invite: PjAgradaCadastroInvite): Promise<void> {
  if (!invite.url) {
    toast.error('Link indisponível — gere um novo convite')
    return
  }
  if (invite.isExpired) {
    toast.error('Link expirado — gere um novo convite')
    return
  }
  try {
    await navigator.clipboard.writeText(invite.url)
    toast.success('Link de cadastro copiado')
  } catch {
    toast.error('Não foi possível copiar o link')
  }
}

function canCopyCadastroInvite(pj: PjAgrada): boolean {
  return Boolean(
    pj.cadastroInvite &&
      pj.cadastroInvite.url &&
      !pj.cadastroInvite.isExpired &&
      (pj.status === 'NAO_PREENCHIDO' || pj.status === 'NAO_CONCLUIDO'),
  )
}

export function PjAgradaTable({
  prestadores,
  loading,
  search,
  onSelect,
  onEdit,
  onDelete,
  onInvite,
}: Props) {
  const showRowActions = Boolean(onEdit || onDelete)

  if (!loading && prestadores.length === 0) {
    return (
      <div className='space-y-4'>
        <GestorListTableShell
          empty
          emptyIcon={
            <Briefcase size={48} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
          }
          emptyTitle={search ? 'Nenhum funcionário encontrado' : 'Nenhum funcionário PJ cadastrado'}
          emptyHint={
            search
              ? 'Ajuste a busca ou os filtros aplicados.'
              : 'Gere um link de cadastro e envie ao colaborador para preenchimento.'
          }
        >
          <table className='w-full' />
        </GestorListTableShell>
        {!search && onInvite && (
          <div className='flex justify-center'>
            <Button type='button' variant='primary' icon={<Link2 size={14} />} onClick={onInvite}>
              Gerar link de cadastro
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <GestorListTableShell
      loading={loading}
      loadingMessage='Carregando funcionários…'
      empty={!loading && prestadores.length === 0}
      emptyIcon={
        <Briefcase size={48} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
      }
      emptyTitle={search ? 'Nenhum funcionário encontrado' : 'Nenhum funcionário PJ cadastrado'}
      emptyHint={
        search
          ? 'Ajuste a busca ou os filtros aplicados.'
          : 'Gere um link de cadastro e envie ao colaborador para preenchimento.'
      }
    >
      <table className='w-full'>
        <thead>
          <tr className='border-b border-[var(--glass-border)]'>
            {[
              'Funcionário',
              'Contato',
              'Cargo',
              'Pagamento',
              'Status',
              'Ações',
            ].map((h) => (
              <th
                key={h || 'nav'}
                className={GESTOR_TABLE_TH_CLASS}
                style={{ fontSize: 'var(--font-size-label)' }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {prestadores.map((pj) => {
            const docs = documentLine(pj)
            const pagamento = formatContaBanco(pj)
            return (
              <tr
                key={pj.id}
                className={GESTOR_TABLE_TR_CLASS}
                onClick={() => onSelect(pj)}
              >
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-3'>
                    <GestorTableAvatar name={pj.nomeCompleto} />
                    <div className='min-w-0'>
                      <p
                        className='truncate font-medium text-[var(--font-color-body)]'
                        style={{ fontSize: 'var(--font-size-sm)' }}
                      >
                        {pj.nomeCompleto}
                      </p>
                      {pj.razaoSocial && (
                        <p className='mt-0.5 truncate text-[11px] text-[var(--font-color-muted)]'>
                          {pj.razaoSocial}
                        </p>
                      )}
                      {docs && (
                        <p className='mt-0.5 font-mono text-[11px] text-[var(--font-color-muted)]'>
                          {docs}
                        </p>
                      )}
                    </div>
                  </div>
                </td>
                <td className='px-4 py-3'>
                  <p
                    className='text-[var(--font-color-body)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {isPlaceholderPhone(pj.telefone) ? '—' : formatPhone(pj.telefone)}
                  </p>
                  {pj.email && (
                    <p className='mt-0.5 truncate max-w-[200px] text-[11px] text-[var(--font-color-muted)]'>
                      {pj.email}
                    </p>
                  )}
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {pj.areaAtuacao || '—'}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='block max-w-[180px] truncate text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                    title={pagamento !== '—' ? pagamento : undefined}
                  >
                    {pagamento}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <div className='space-y-1'>
                    <span
                      className={cn(
                        'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                        PJ_AGRADA_STATUS_CLASS[pj.status],
                      )}
                    >
                      {PJ_AGRADA_STATUS_LABELS[pj.status]}
                    </span>
                    {pj.cadastroInvite &&
                      (pj.status === 'NAO_PREENCHIDO' || pj.status === 'NAO_CONCLUIDO') && (
                        <p
                          className={cn(
                            'text-[11px]',
                            pj.cadastroInvite.isExpired
                              ? 'text-red-400'
                              : 'text-[var(--font-color-muted)]',
                          )}
                        >
                          {formatInviteTimeRemaining(
                            pj.cadastroInvite.expiresAt,
                            pj.cadastroInvite.isExpired,
                          )}
                        </p>
                      )}
                  </div>
                </td>
                <td className='px-4 py-3' onClick={(e) => e.stopPropagation()}>
                  <div className='flex items-center gap-0.5'>
                    {canCopyCadastroInvite(pj) && pj.cadastroInvite && (
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='h-8 w-8 p-0 text-[var(--text-muted)] hover:text-[var(--brand-primary)]'
                        title={`Copiar link de cadastro${formatInviteTimeRemaining(pj.cadastroInvite.expiresAt, pj.cadastroInvite.isExpired) ? ` · ${formatInviteTimeRemaining(pj.cadastroInvite.expiresAt, pj.cadastroInvite.isExpired)}` : ''}`}
                        onClick={() => void copyCadastroInviteLink(pj.cadastroInvite!)}
                      >
                        <Copy size={14} />
                      </Button>
                    )}
                    {onEdit && (
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='h-8 w-8 p-0 text-[var(--text-muted)] hover:text-[var(--brand-primary)]'
                        onClick={() => onEdit(pj)}
                        title='Editar'
                      >
                        <Pencil size={14} />
                      </Button>
                    )}
                    {onDelete && (
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='h-8 w-8 p-0 text-[var(--text-muted)] hover:text-red-500'
                        onClick={() => onDelete(pj.id)}
                        title='Excluir'
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                    {!canCopyCadastroInvite(pj) && !showRowActions && (
                      <ChevronRight size={14} className='text-[var(--text-muted)]' aria-hidden />
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </GestorListTableShell>
  )
}

export function PjAgradaDetailSummary({ prestador }: { prestador: PjAgrada }) {
  return (
    <div className='space-y-4 text-sm text-[var(--font-color-body)]'>
      <div>
        <p className='mb-1 text-xs uppercase text-[var(--font-color-label)]'>Endereço</p>
        <p>{formatEndereco(prestador)}</p>
      </div>
      <div>
        <p className='mb-1 text-xs uppercase text-[var(--font-color-label)]'>Dados bancários</p>
        <p>{formatContaBanco(prestador)}</p>
      </div>
      {prestador.observacoes && (
        <div>
          <p className='mb-1 text-xs uppercase text-[var(--font-color-label)]'>Observações</p>
          <p className='whitespace-pre-wrap'>{prestador.observacoes}</p>
        </div>
      )}
    </div>
  )
}
